import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Users, RefreshCw, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";

function fmtMoney(v, currency) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${currency || ""} ${Math.round(v).toLocaleString()}`;
  }
}

function pct(v) {
  return v == null ? "—" : `${Math.round(v * 100)}%`;
}

const QUOTA_SOURCE_LABEL = {
  explicit: "explicit",
  target_split: "target ÷ owners",
  unset: "not set",
};

export default function TeamPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/metrics/latest");
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const recompute = async () => {
    setBusy(true);
    try {
      await api.post("/metrics/recompute");
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10 text-sm text-slate-500" data-testid="team-loading">
        Loading team…
      </div>
    );
  }

  const team = !data.empty ? (data.metrics.team || []) : [];
  const cur = !data.empty ? (data.metrics.currency || "USD") : "USD";
  const withDeals = team.filter((t) => t.has_deals !== false);
  const withoutDeals = team.filter((t) => t.has_deals === false);

  // Truly empty: no metrics doc at all, OR metrics exist but the HubSpot portal
  // has no owners synced yet either.
  if (data.empty || team.length === 0) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10" data-testid="team-empty-state">
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center flex flex-col items-center">
          <Users className="h-10 w-10 text-slate-400" />
          <h2 className="mt-4 text-xl font-semibold text-slate-900">No team data yet</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-sm">
            {data.empty
              ? "Complete setup (Connect HubSpot + confirm stage mapping) to see per-owner metrics."
              : "No users found in the connected HubSpot portal yet — run a sync from Connect to pull the owner list."}
          </p>
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => nav("/dashboard/connect")}
              data-testid="team-setup-cta"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
            >
              Set up now
            </button>
            <button
              onClick={recompute}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
              Recompute
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto w-full p-6 lg:p-10 space-y-8" data-testid="team-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Team</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            Per-owner view
          </h1>
          <p className="mt-3 text-slate-500 max-w-xl">
            Open pipeline, coverage, win rate, cycle length, and forecast reliability. Snapshot{" "}
            {data.metrics.metric_date}. Set individual quotas in Settings; otherwise the account
            target is split evenly.
          </p>
        </div>
        <button
          onClick={recompute}
          disabled={busy}
          data-testid="team-recompute-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
          Recompute
        </button>
      </header>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="team-list">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-slate-100 bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
          <div className="col-span-3">Owner</div>
          <div className="col-span-2 text-right">Open pipeline</div>
          <div className="col-span-2 text-right">Coverage</div>
          <div className="col-span-2 text-right">Win rate</div>
          <div className="col-span-1 text-right">Cycle</div>
          <div className="col-span-2 text-right">Forecast reliability</div>
        </div>
        {team.map((t) => {
          const noDeals = t.has_deals === false;
          return (
            <div
              key={t.owner_id}
              className={`grid grid-cols-12 px-6 py-4 border-b border-slate-100 last:border-b-0 items-center transition-colors ${
                noDeals ? "bg-slate-50/40 hover:bg-slate-50/80" : "hover:bg-slate-50/60"
              }`}
              data-testid={`team-row-${t.owner_id}`}
            >
              <div className="col-span-3">
                <div className={`text-sm font-semibold truncate ${noDeals ? "text-slate-500" : "text-slate-900"}`}>
                  {t.owner_name}
                </div>
                <div className="text-[11px] text-slate-500">
                  {noDeals ? (
                    <span className="italic text-slate-400" data-testid={`team-row-${t.owner_id}-nodeals`}>
                      no deals assigned
                    </span>
                  ) : (
                    <>
                      quota {t.quota != null ? fmtMoney(t.quota, cur) : "—"}
                      {" · "}
                      <span className="text-slate-400">{QUOTA_SOURCE_LABEL[t.quota_source] || t.quota_source}</span>
                    </>
                  )}
                </div>
              </div>
              <div className={`col-span-2 text-right text-sm font-semibold number-tabular ${noDeals ? "text-slate-400" : "text-slate-900"}`}>
                {noDeals ? fmtMoney(0, cur) : fmtMoney(t.open_pipeline_value, cur)}
              </div>
              <div className={`col-span-2 text-right text-sm font-semibold number-tabular ${
                noDeals || t.coverage == null
                  ? "text-slate-400"
                  : t.coverage >= 3
                  ? "text-emerald-600"
                  : t.coverage >= 2
                  ? "text-slate-900"
                  : "text-amber-600"
              }`}>
                {noDeals ? "—" : t.coverage != null ? `${t.coverage.toFixed(1)}x` : "—"}
              </div>
              <div className={`col-span-2 text-right text-sm number-tabular ${noDeals ? "text-slate-400" : "text-slate-900"}`}>
                {noDeals ? "—" : pct(t.win_rate)}
                {!noDeals && (
                  <span className="text-[10px] text-slate-400 ml-1">({t.win_rate_sample})</span>
                )}
              </div>
              <div className={`col-span-1 text-right text-sm number-tabular ${noDeals ? "text-slate-400" : "text-slate-700"}`}>
                {noDeals ? "—" : t.median_cycle_days != null ? `${Math.round(t.median_cycle_days)}d` : "—"}
              </div>
              <div className={`col-span-2 text-right text-sm number-tabular ${noDeals ? "text-slate-400" : "text-slate-700"}`}>
                {noDeals ? "—" : t.forecast_reliability_days != null ? `±${t.forecast_reliability_days}d` : "—"}
                {!noDeals && (
                  <span className="text-[10px] text-slate-400 ml-1">({t.forecast_reliability_sample})</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {withoutDeals.length > 0 && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900 flex items-start gap-2"
          data-testid="team-assign-hint"
        >
          <Info className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            {withDeals.length === 0
              ? `${withoutDeals.length} ${withoutDeals.length === 1 ? "owner" : "owners"} synced from HubSpot but none have deals yet. `
              : `${withoutDeals.length} of ${team.length} owners have no deals assigned. `}
            Assign deal owners in HubSpot to see per-rep performance here.
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 flex items-start gap-2" data-testid="team-legend">
        <Info className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold text-slate-900">Coverage</span> = open pipeline ÷ quota.{" "}
          <span className="font-semibold text-slate-900">Cycle</span> = median days from create to close.{" "}
          <span className="font-semibold text-slate-900">Forecast reliability</span> = average absolute
          gap in days between the close date set 30 days before close and the actual close date, over
          the last 6 months of closed deals. Lower is better. Small samples in parentheses are shown for context, not judgment.
        </div>
      </div>

      {err && <div className="text-sm text-red-600" data-testid="team-error">{err}</div>}
    </div>
  );
}
